/**
 * Framing helpers (aviation data spec §8): the pan/tilt rate an operator must follow, and the lens
 * needed for a target frame fill. Pure; local tangent-plane (ENU) maths about the observer, accurate
 * to well under 1 % inside ~50 km.
 *
 * Framing example from the spec: "at current range, 200 mm on S35 → subject fills 38 % of frame".
 * Heron span: 14.3 m (HERON_SPAN_M).
 */
import type { LatLon } from './types.ts';
import { MPS_PER_KT, M_PER_FT, bearingDeg, distanceM } from './geo.ts';

/** Heron HX-1 wingspan (m). */
export const HERON_SPAN_M = 14.3;

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

export interface FramingBody {
  pos: LatLon;
  /** metres MSL (null = same height as the other body). */
  altM: number | null;
  gsKt?: number | null;
  /** degrees true */
  trackDeg?: number | null;
  vsFpm?: number | null;
}

export interface AngularRates {
  /** Total line-of-sight rotation rate (°/s) — how fast the subject crosses the sky. */
  totalDegS: number;
  /** Azimuth (pan) rate, °/s, + = clockwise (panning right). */
  panDegS: number;
  /** Elevation (tilt) rate, °/s, + = tilting up. */
  tiltDegS: number;
  /** Slant range (m). */
  rangeM: number;
}

function velocityENU(b: FramingBody): [number, number, number] {
  const gs = b.gsKt ?? 0;
  const vu = ((b.vsFpm ?? 0) * M_PER_FT) / 60;
  if (!(gs > 0) || b.trackDeg == null || !Number.isFinite(b.trackDeg)) return [0, 0, vu];
  const v = gs * MPS_PER_KT;
  return [v * Math.sin(b.trackDeg * D2R), v * Math.cos(b.trackDeg * D2R), vu];
}

/** Pan / tilt / total angular rate of `target` as seen from `observer` (either may move). */
export function angularRates(observer: FramingBody, target: FramingBody): AngularRates {
  const d = distanceM(observer.pos, target.pos);
  const brg = d > 0 ? bearingDeg(observer.pos, target.pos) * D2R : 0;
  const e = d * Math.sin(brg);
  const n = d * Math.cos(brg);
  const u = observer.altM != null && target.altM != null ? target.altM - observer.altM : 0;
  const [tvE, tvN, tvU] = velocityENU(target);
  const [ovE, ovN, ovU] = velocityENU(observer);
  const vE = tvE - ovE;
  const vN = tvN - ovN;
  const vU = tvU - ovU;
  const h2 = e * e + n * n;
  const r2 = h2 + u * u;
  const range = Math.sqrt(r2);
  if (!(r2 > 1e-6)) return { totalDegS: 0, panDegS: 0, tiltDegS: 0, rangeM: 0 };
  // |r × v| / |r|²
  const cx = n * vU - u * vN;
  const cy = u * vE - e * vU;
  const cz = e * vN - n * vE;
  const total = Math.sqrt(cx * cx + cy * cy + cz * cz) / r2;
  const h = Math.sqrt(h2);
  const pan = h2 > 1e-6 ? (n * vE - e * vN) / h2 : 0; // d/dt atan2(e, n)
  const hDot = h > 1e-3 ? (e * vE + n * vN) / h : 0;
  const tilt = (h * vU - u * hDot) / r2; // d/dt atan2(u, h)
  return { totalDegS: total * R2D, panDegS: pan * R2D, tiltDegS: tilt * R2D, rangeM: range };
}

/** Line-of-sight angular rate (°/s) the operator must track. */
export function angularRateDegPerSec(observer: FramingBody, target: FramingBody): number {
  return angularRates(observer, target).totalDegS;
}

/** Angular size (degrees) of a span at a range. */
export function angularSizeDeg(spanM: number, rangeM: number): number {
  if (!(rangeM > 0)) return 180;
  return 2 * Math.atan(spanM / (2 * rangeM)) * R2D;
}

/** % of frame width filled by `spanM` at `rangeM` with a focal length on a sensor width (rectilinear). */
export function fillPct(spanM: number, rangeM: number, focalMm: number, sensorWidthMm: number): number {
  if (!(rangeM > 0) || !(focalMm > 0) || !(sensorWidthMm > 0)) return NaN;
  const hfov = 2 * Math.atan(sensorWidthMm / (2 * focalMm));
  const coverage = 2 * rangeM * Math.tan(hfov / 2); // = rangeM × sensor / focal
  return (spanM / coverage) * 100;
}

/** Focal length (mm) that makes `spanM` fill `fillPctTarget`% of the frame width at `rangeM`. */
export function lensForFill(spanM: number, rangeM: number, sensorWidthMm: number, fillPctTarget: number): number {
  if (!(spanM > 0) || !(rangeM > 0) || !(sensorWidthMm > 0) || !(fillPctTarget > 0)) return NaN;
  return (sensorWidthMm * rangeM * (fillPctTarget / 100)) / spanM;
}

/** "200 mm → 38 % of frame" style helper text. */
export function describeFill(spanM: number, rangeM: number, focalMm: number, sensorWidthMm: number): string {
  const p = fillPct(spanM, rangeM, focalMm, sensorWidthMm);
  if (!Number.isFinite(p)) return '—';
  return `${Math.round(focalMm)} mm → subject fills ${p >= 100 ? '>100' : Math.round(p)}% of frame`;
}
